import { useNewCode } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { Item, PopupDropdownMenu } from '@calendly/ui/components/menu';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetUserQuery } from 'common';
import { useSprig } from 'components/sprig';
import { Segment } from 'core/analytics/segment';
import Settings from 'core/settings';
import { shouldShowGettingStartedHelpEntry, useGettingStartedGuideRemovalVariant } from 'features/growth_experiments';
import { useUserDismissed, useIsUserGuideViewable, useToggleUserGuide } from 'features/user_guide';
import { isCookiesAllowedSelector } from 'selectors/gdpr';
import { DesktopChatButton } from './chat_button';
import { NavigationAnchorItem, NavigationIconContainer, NavigationLabel } from './elements';
import { useLeftNavigationState } from './hooks';
import { HelpIcon, CaretDownIcon, ExternalIcon, ChatIcon, GetStartedGuideIcon } from './icons';
const containerCss = "cai97qb";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const styledDropdownMenuCss = "s1a00std";
const StyledDropdownMenu = forwardRef((props, ref) => <PopupDropdownMenu ref={ref} {...props} className={classNames(styledDropdownMenuCss, props.className)} />);
const styledFixedContainerCss = "s8bhxrs";
const StyledFixedContainer = forwardRef((props, ref) => {
  const fixed = true;
  return <FixedContainer ref={ref} {...props} fixed={fixed} className={classNames(styledFixedContainerCss, props.className)} />;
});
const MENU_ID = 'main-help-menu';
const MENU_TOGGLE_ID = 'main-help-menu-toggle';
export const HelpItem = () => {
  const [toggle, Wrapper, isExpanded] = useDropdown();
  const {
    data: {
      id: userId,
      is_pretending: isPretending
    }
  } = useGetUserQuery();
  const functionalCookiesAllowed = useSelector(isCookiesAllowedSelector('functional'));
  const leftNavigationState = useLeftNavigationState();
  const isNavMinimized = leftNavigationState === 'phone-collapsed' || leftNavigationState === 'minimized';
  const isAdaUxFixesEnabled = useNewCode('monetization_ada_ux_improvements')?.enabled;
  const isAdaAvailable = Settings.ada?.enabled && !isAdaUxFixesEnabled;
  const isChatAvailable = (isAdaAvailable || Settings.intercom?.enabled) && !isPretending && functionalCookiesAllowed;
  const isCommunityAvailable = !isPretending;
  const userGuideViewable = useIsUserGuideViewable();
  const [guideDismissed, setGuideDismissed] = useUserDismissed();
  const [guideOpen, toggleGuide] = useToggleUserGuide();
  const isPhone = useDeviceType('isPhone');
  const gsgRemovalVariant = useGettingStartedGuideRemovalVariant();
  const showGetStartedGuide = shouldShowGettingStartedHelpEntry({
    viewable: userGuideViewable,
    open: guideOpen,
    dismissed: guideDismissed,
    variant: gsgRemovalVariant
  }) && !isPhone;
  const handleUserGuideClicked = () => {
    toggleGuide();
    if (guideDismissed) {
      setGuideDismissed(false);
    }
  };
  const sprig = useSprig();
  const showSprigSurvey = () => {
    sprig.identifyAndTrack({
      eventName: 'homepage_give_feedback_form',
      userId
    });
  };
  return <Container>
      <NavigationAnchorItem data-calendly-label="help-dropdown" as={BareButton} onClick={toggle} aria-expanded={isExpanded} tooltip={{
      text: isNavMinimized ? I18n.t('publisher.left_navigation.help') : null,
      placement: 'right'
    }}>
        <NavigationIconContainer>
          <HelpIcon />
        </NavigationIconContainer>
        <NavigationLabel>
          {I18n.t('publisher.left_navigation.help')}
          {!isNavMinimized && <CaretDownIcon />}
        </NavigationLabel>
      </NavigationAnchorItem>
      <StyledFixedContainer>
        <Wrapper>
          <StyledDropdownMenu role="menu" id={MENU_ID} aria-labelledby={MENU_TOGGLE_ID}>
            <Item as="a" role="menuitem" href="/help" target="_blank" rel="noopener noreferrer" onClick={() => Segment.track('help_menu.help_center_clicked', {
            user_id: userId
          })}>
              <ExternalIcon />
              {I18n.t('publisher.menu.help_menu.help_center')}
            </Item>
            {isCommunityAvailable && <Item as="a" role="menuitem" href="/sso/insided_community" target="_blank" rel="noopener noreferrer">
                  <ExternalIcon />
                  {I18n.t('publisher.menu.help_menu.calendly_community')}
                </Item>}
            <Item as="a" target="_blank" href="/release-notes" role="menuitem" rel="noreferrer noopener" data-calendly-label="whats-new-link">
              <ExternalIcon />
              {I18n.t('publisher.menu.new_dropdown.whats_new')}
            </Item>
            {showGetStartedGuide && <Item role="menuitem" onClick={handleUserGuideClicked}>
                <GetStartedGuideIcon />
                {I18n.t('publisher.menu.help_menu.get_started_guide')}
              </Item>}
            {isChatAvailable && <DesktopChatButton />}
            <Item role="menuitem" onClick={() => showSprigSurvey()}>
              <ChatIcon />
              {I18n.t('publisher.menu.help_menu.give_feedback')}
            </Item>
          </StyledDropdownMenu>
        </Wrapper>
      </StyledFixedContainer>
    </Container>;
};

require("./help_item.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./help_item.js");